import React, { Component } from 'react';
import { Location } from 'history';
import PrintableReturnInfo, { PrintHistoryState, GRBillState } from './PrintableReturnInfo';
import { Alert, Button } from 'reactstrap';
import { History } from 'history';
import { grossReceiptTypes } from '../utility/Constants';
import { GrossReceiptReturn, ReturnEntryStateRoomOccupancy, ReturnEntryStateSplitValueVehicle } from '../types/GrossReceiptReturnTypes';
import { isRoomOccupancy, isFoodBeverage, isSplitValueVehicle } from '../utility/TypeGuards';
import { FoodBeverageReturnLocation, ReturnLocation, RoomOccupancyLocation, SplitVehicleLocation } from '../types/LocationTypes';
import { Redirect } from 'react-router-dom';
import LoadingButtonBase from './common/LoadingButtonBase';
import { post } from '../utility/FetchFunctions';
import moment from 'moment';
import FloatingDismissibleAlert from './common/FloatingDismissableAlert';

export interface VerifyReturnProps {
    location: Location<VerifyHistoryState>;
    history: History;
};

export interface VerifyHistoryState extends PrintHistoryState {
    loading: boolean;
    error: string;
    countyPhoneNumber: string;
}

export interface FoodBeverageVerifyHistoryState extends VerifyHistoryState {
    returnTypeCode: grossReceiptTypes.foodBev;
    locations?: FoodBeverageReturnLocation[];
}

export interface RoomOccupancyVerifyHistoryState extends VerifyHistoryState {
    returnTypeCode: grossReceiptTypes.roomOccupancy;
    locations?: RoomOccupancyLocation[];
}

export interface SplitValueVehicleVerifyHistoryState extends VerifyHistoryState {
    returnTypeCode: grossReceiptTypes.vehicle;
    locations?: SplitVehicleLocation[];
}

export function verifyIsRoomOccupancy(grReturn: (VerifyHistoryState | FoodBeverageVerifyHistoryState | RoomOccupancyVerifyHistoryState | SplitValueVehicleVerifyHistoryState)): grReturn is RoomOccupancyVerifyHistoryState {
    return grReturn.returnTypeCode === grossReceiptTypes.roomOccupancy;
}
export function verifyIsFoodBeverage(grReturn: (VerifyHistoryState | FoodBeverageVerifyHistoryState | RoomOccupancyVerifyHistoryState | SplitValueVehicleVerifyHistoryState)): grReturn is FoodBeverageVerifyHistoryState {
    return grReturn.returnTypeCode === grossReceiptTypes.foodBev;
}

export function verifyIsSplitValueVehicle(grReturn: (VerifyHistoryState | FoodBeverageVerifyHistoryState | RoomOccupancyVerifyHistoryState | SplitValueVehicleVerifyHistoryState), yearFor: number | null): grReturn is SplitValueVehicleVerifyHistoryState {
    return grReturn.returnTypeCode === grossReceiptTypes.vehicle && yearFor !== null && grReturn.splitVehicleUnderByYear[yearFor] !== null;
}

export default class VerifyReturn extends Component<VerifyReturnProps, VerifyHistoryState | FoodBeverageVerifyHistoryState | RoomOccupancyVerifyHistoryState | SplitValueVehicleVerifyHistoryState> {
    constructor(props: Readonly<VerifyReturnProps>) {
        super(props);
        this.state = this.props.location.state;
    }

    static displayName = VerifyReturn.name;

    render() {
        return (
            <div>
                {
                    this.props.location.state === undefined || this.props.location.state.accountNum === undefined ?
                        (
                            <Redirect to='/' />
                        ) :
                        this.verifyComponent
                }
            </div>
        );
    }

    get editValues() {
        const returnValue: GrossReceiptReturn | ReturnEntryStateRoomOccupancy | ReturnEntryStateSplitValueVehicle = {
            accountNum: this.state.accountNum.toString() ?? '',
            pin: this.state.pin ?? '',
            excess: this.state.excessTaxAmount?.toString() ?? '',
            dueDate: moment.utc(this.state.dueDate).format('YYYY-MM-DD') ?? '',
            ownerName: this.state.ownerName ?? '',
            locations: this.state.locations || [],
            ownerAddress: this.state.ownerAddressLine1 ?? '',
            grossReceiptType: this.state.returnTypeDescription,
            grossReceiptTypeCode: this.state.returnTypeCode,
            creditRemaining: this.state.creditRemaining ?? 0,
            creditApplied: this.state.creditApplied.toString() ?? '0',
            creditRefunded: this.state.creditRefunded.toString() ?? '0',
            returnMonth: this.state.returnMonth,
            returnYear: this.state.returnYear,
            returnFiscalYear: this.state.returnFiscalYear,
            filedBy: this.state.filedBy,
            splitVehicleUnderByYear: this.state.splitVehicleUnderByYear,
            splitVehicleOverByYear: this.state.splitVehicleOverByYear
        };
        if (verifyIsRoomOccupancy(this.state) && isRoomOccupancy(returnValue)) {
            returnValue.locations = this.state.locations ?? [];
        }
        else if (verifyIsFoodBeverage(this.state) && isFoodBeverage(returnValue)) {
            returnValue.locations = this.state.locations ?? [];
        }
        else if (verifyIsSplitValueVehicle(this.state, this.state.returnFiscalYear) && isSplitValueVehicle(returnValue, this.state.returnFiscalYear)) {
            returnValue.locations = this.state.locations ?? [];
        }
        return returnValue;
    };

    private readonly editReturn = (): void => {
        this.props.history.push('/enter-return', this.editValues);
    }

    private readonly submitReturn = async () => {
        this.setState({ loading: true, error: '' });
        try {
            let response = await post('api/GrossReceiptReturn/SubmitGrossReceiptReturnAndCreateBill', this.state);
            if (response.ok) {
                let newBill = await response.getValue?.();
                this.setState({
                    bill: newBill as GRBillState
                });

                this.props.history.push('/enter-return/print', this.state);
            } else {
                const error = `An error occurred while submitting your return. Please contact the tax office at ${this.state.countyPhoneNumber}.`;
                this.setState({
                    error
                });
            }
        } finally {
            this.setState({ loading: false });
        }
    }

    get error() {
        return this.state.error
            ? <FloatingDismissibleAlert color="danger" clearError={() => this.setState({ error: '' })} >
                {this.state.error }
            </FloatingDismissibleAlert>
            : <></>;
    };

    get info() {
        return (
            <>
                <Alert className="centered-container" color="info">
                    Please verify the return information before submitting.
                    After submission, you will have to contact the county tax office to change your return.
                </Alert>
            </>
        )
    };
    get verifyComponent() {
        return (
            <>
                {this.info}
                <PrintableReturnInfo {...this.props.location.state} />
                {this.error}
                <div className="print-button-container">
                    <Button id="btnRedoReturn" className="edit-button-left" onClick={this.editReturn}>Redo Return</Button>
                    <LoadingButtonBase loading={this.state.loading} label="Submit" className="submit-button-right search-button" onClick={this.submitReturn}></LoadingButtonBase>
                </div>
            </>);
    }
}